import { GiftIcon } from "@xola/icons";
import { FormField, isEquipmentBased } from "common";
import { useFormikContext } from "formik";
import React, { useEffect } from "react";
import { Experience, GiftOffer, GiftOfferType, PurchaseRule } from "types";
import { useSeller } from "../../hooks/useAccount";
import { GiftOfferLineItem, useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { NoTranslate } from "../Common/NoTranslate";
import { ProductImage } from "../Common/ProductImage";
import { CurrencyInputField } from "../Field/CurrencyInputField";
import { getTerms } from "../Product/EditGiftOffer";
import { CreatePurchaseGiftDemographicPicker } from "./CreatePurchaseGiftDemographicPicker";
import { CreatePurchaseGiftEquipmentPicker } from "./CreatePurchaseGiftEquipmentPicker";
import { CreatePurchaseGiftRestrictions } from "./CreatePurchaseGiftRestrictions";
import { CreatePurchaseGuestQuantityPicker } from "./CreatePurchaseGuestQuantityPicker";
import { CreatePurchasePersonalizeGiftFields } from "./CreatePurchasePersonalizeGiftFields";
import { useProduct } from "@/hooks/resources/useProduct";
import { useResource } from "@/hooks/useResource";

interface CreatePurchaseGiftFieldsProps {
    product: GiftOffer;
}

export interface FormValues {
    amount: number;
}

export const CreatePurchaseGiftFields = ({ product }: CreatePurchaseGiftFieldsProps) => {
    const { gift: giftPreferences } = useSeller().preferences;
    const activeItemIndex = useCreatePurchaseStore((state) => state.activeItemIndex);

    const setPurchase = useCreatePurchaseStore((state) => state.actions.setPurchase);
    const activeProduct = useCreatePurchaseStore((state) => state.getActiveProduct<GiftOffer>()) ?? product;
    const isScannedGiftCardItem = useCreatePurchaseStore((state) => {
        return state.getActiveItem().isScannedGiftCard;
    });
    const { purchaseRule: purchaseRuleFromProduct } = product;

    const { data: experience } = useProduct<Experience>(activeProduct.experience?.id);

    const { data: fetchedPurchaseRule } = useResource<PurchaseRule>(["purchaseRules", activeProduct?.purchaseRule?.id]);

    const purchaseRule = purchaseRuleFromProduct?.actions ? purchaseRuleFromProduct : fetchedPurchaseRule;

    const { values } = useFormikContext<FormValues>();

    const isChooseAnAmount = activeProduct.baseGiftProduct && activeProduct.system;
    const isVariable = getTerms(product, purchaseRule).type === GiftOfferType.VARIABLE;

    useEffect(() => {
        const itemLineItem = useCreatePurchaseStore.getState().getActiveItem().lineItems?.[0];
        const itemLineItemAmount = (itemLineItem as GiftOfferLineItem)?.amount;
        const amount = values.amount || (itemLineItemAmount ?? 1);

        setPurchase((draft) => {
            const draftLineItem = draft.items[activeItemIndex].lineItems[0] as GiftOfferLineItem;
            if (draftLineItem) {
                draftLineItem.amount = amount;
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.amount]);

    return (
        <>
            <div className="space-y-3 rounded bg-gray-lighter py-5 px-6">
                <div className="flex items-center space-x-1">
                    <div className="flex">
                        {isChooseAnAmount ? (
                            <div className="mr-3 flex-shrink-0 rounded-lg border border-gray bg-white p-2">
                                <GiftIcon size="medium" />
                            </div>
                        ) : (
                            <ProductImage product={activeProduct} size="small" className="!mr-1" />
                        )}
                    </div>

                    <NoTranslate as="h3">{activeProduct.name}</NoTranslate>
                </div>
            </div>

            {activeProduct.experience?.id ? (
                experience && isEquipmentBased(experience) ? (
                    <CreatePurchaseGiftEquipmentPicker />
                ) : giftPreferences?.showDemographics ? (
                    <CreatePurchaseGiftDemographicPicker />
                ) : (
                    <CreatePurchaseGuestQuantityPicker experienceId={activeProduct.experience.id} />
                )
            ) : isChooseAnAmount || isVariable ? (
                <FormField name="amount" label="Gift Amount">
                    <CurrencyInputField isRequired min="0" disabled={isScannedGiftCardItem} />
                </FormField>
            ) : null}

            {!activeProduct.experience?.id && !isChooseAnAmount ? (
                <CreatePurchaseGiftRestrictions product={activeProduct} purchaseRule={purchaseRule} />
            ) : null}

            <hr className="text-gray-light" />

            <CreatePurchasePersonalizeGiftFields />
        </>
    );
};
